import { forwardRef } from 'react';

export type SwitchTone = 'accent' | 'green';

export interface SwitchProps {
  /** On/off state. */
  checked: boolean;
  /** Called with the next state when toggled. */
  onChange: (next: boolean) => void;
  /** Blocks interaction and dims the control. */
  disabled?: boolean;
  /** Fill color when on. Default `accent`; use `green` for enabled/success semantics. */
  tone?: SwitchTone;
  /** Accessible name — required (the switch has no visible text). */
  'aria-label': string;
  title?: string;
  style?: React.CSSProperties;
}

const TONE_VAR: Record<SwitchTone, string> = {
  accent: 'var(--accent)',
  green: 'var(--green)',
};

/**
 * Switch — the canonical sliding on/off toggle (track + knob). Replaces the
 * per-page hand-rolled `<button>` switches that each re-implemented the same
 * 56×32 track with a translating knob. Exposes proper `role="switch"` +
 * `aria-checked`; `tone` picks the on-fill (`accent` default, `green` for
 * enabled/success semantics).
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, disabled = false, tone = 'accent', title, style, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={rest['aria-label']}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        height: 32,
        width: 56,
        alignItems: 'center',
        borderRadius: 9999,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: checked ? TONE_VAR[tone] : 'var(--bg-tertiary)',
        transition: 'background 0.2s',
        ...style,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          height: 24,
          width: 24,
          borderRadius: '50%',
          background: checked ? '#fff' : 'var(--text-muted)',
          transition: 'transform 0.2s',
          transform: checked ? 'translateX(28px)' : 'translateX(4px)',
        }}
      />
    </button>
  );
});
